<template>
  <div id="app">
    <Tabs v-model="projectionMode">
      <Tab title="Historical" />
      <Tab title="Projected" />
      <ControlGroup>
        <DataGetter
          :url="`${apiRoute}/list/parameter`"
          :params="{
            category: projected
              ? ['Projection-APS', 'Projection-STEPS']
              : ['Historical', 'Historical_MoMo'],
          }"
          :child-element-type="null"
          v-slot="getterData"
          @fetched="(data) => checkValid('parameter', data)"
        >
          <Dropdown
            key="parameterdd"
            label="Show"
            v-model="parameter"
            :options="sortData(getterData.data)"
          />
        </DataGetter>
        <DataGetter
          key="vehicletypegetter"
          v-if="parameter !== 'EV charger points'"
          :url="`${apiRoute}/list/mode`"
          :params="{ region, category, parameter }"
          :child-element-type="null"
          v-slot="getterData"
          @fetched="(data) => checkValid('vehicleType', data)"
        >
          <Dropdown
            key="vehicletypedd"
            label="Transport mode"
            v-model="vehicleType"
            reset-on-invalid
            :options="
              sortData(getterData.data).filter((option) => option !== 'NA')
            "
          />
        </DataGetter>
        <DataGetter
          :url="`${apiRoute}/list/region`"
          :params="{ parameter, category }"
          :child-element-type="null"
          v-slot="getterData"
          @fetched="(data) => checkValid('region', data)"
        >
          <Dropdown
            key="regionsdd"
            label="Region"
            v-model="region"
            reset-on-invalid
            :options="sortData(getterData.data)"
          />
        </DataGetter>
        <Dropdown
          v-if="projected"
          key="projection"
          label="Projection scenario"
          v-model="projection"
          :options="projections"
        />
      </ControlGroup>
      <div class="container">
        <DataGetter
          :url="apiRoute"
          v-slot="{ data, isLoading }"
          :params="urlParams"
          @fetched="setUnit"
          class="m-block m-block--charts m-block--charts-full"
        >
          <DataSlicer
            :data="[...data]"
            x-axis-dimension="year"
            y-axis-dimension="powertrain"
            value-dimension="value"
            v-slot="{ slicedData }"
            class="m-chart-block"
            key="chartslicer"
            v-if="!isLoading"
          >
            <header>
              <span class="f-ui-2-bold">
                {{ parameter.trim() }},
                {{
                  parameter !== "EV charger points"
                    ? `${lowerCase(vehicleType)},`
                    : ""
                }}
                {{ region }}, {{ projected ? `${projection} scenario` : "" }}
                {{ years[0] }}-{{ years[years.length - 1] }}
              </span>
            </header>
            <p class="m-chart__units">
              {{ `${unit === "percent" ? "%" : ` ${unit}`}` }}
            </p>
            <chart
              v-if="data"
              :key="`${vehicleType}${region}${parameter}chart${
                projected ? projection : 'historical'
              }`"
              :options="{
                ...chartOptions,
                tooltip: {
                  valueSuffix: `${unit === 'percent' ? '%' : ` ${unit}`}`,
                  valueDecimals:
                    slicedData.length > 0 &&
                    slicedData[0].data.length > 0 &&
                    slicedData[0].data[slicedData[0].data.length - 1][1] < 100
                      ? 1
                      : 0,
                },
                series: slicedData.map((series) => ({
                  ...series,
                  index: modeOrder.indexOf(series.name),
                  colorIndex: modeOrder.indexOf(series.name),
                })),
              }"
              class="highcharts-outline-black m-chart__item"
            />
            <small class="m-chart__credit f-ui-3">
              IEA. All rights reserved.
            </small>
          </DataSlicer>
          <div class="m-chart-metadata">
            <div class="m-chart-metadata__copyright">
              <template v-if="notes.length > 0">
                <span class="f-ui-3"> Notes </span>
                <p class="f-ui-3">
                  {{ notes }}
                </p>
              </template>
            </div>
            <div class="m-chart-metadata__actions">
              <StyledButton
                :url="downloadLink"
                primary
                >Download data</StyledButton
              >
            </div>
          </div>
        </DataGetter>
      </div>
    </Tabs>
  </div>
</template>

<script>
/* global Highcharts */
import { Chart } from "highcharts-vue";
import {
  Tabs,
  Tab,
  ControlGroup,
  Dropdown,
  StyledButton,
  DataGetter,
  DataSlicer,
} from "@international-energy-agency/vue-ui";
import options from "@international-energy-agency/vue-ui/highcharts";
Highcharts.setOptions({
  lang: {
    numericSymbols: null,
  },
});

export default {
  name: "App",
  components: {
    DataGetter,
    DataSlicer,
    Chart,
    ControlGroup,
    Dropdown,
    StyledButton,
    Tabs,
    Tab,
  },
  props: {
    test: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      apiRoute: `https://api.${this.test ? "test." : ""}iea.org/evs`,
      vehicleType: "Cars",
      region: "World",
      parameter: "EV sales",
      projectionMode: "Historical",
      projection: "STEPS",
      projections: ["STEPS", "APS"],
      modeOrder: [
        "EV",
        "BEV",
        "PHEV",
        "FCEV",
        "Home chargers",
        "Publicly available fast",
        "Publicly available slow",
        "Work charger",
        "NA",
      ],
      unit: "",
      years: [],
      historicalParameters: [
        "EV stock",
        "EV sales",
        "EV sales share",
        "EV stock share",
        "EV charger points",
        "Oil displacement MLGE",
        "Oil displacement MBD",
      ],
      areaParameters: [
        "EV stock",
        "EV charger points",
        /*
        'EV stock share',
        'EV sales share',
        */
        "Avoided CO2 emissions from EVs",
      ],
      parameterNotes: {
        "Oil displacement":
          "Oil displacement refers to oil products that would have been consumed if the EV fleet were instead composed of internal combustion engines vehicles.",
        "Electricity demand":
          "Electricity demand refers to electricity consumed by the EV fleet.",
        "EV charger points":
          "Total stock of EV charger points. Fast chargers have a charging power above 22 kW. Slow chargers have a charging power below 22 kW.",
      },
      vehicleTypeNotes: {
        Vans: "Vans are defined as light commercial vehicles with gross vehicle weight (GVW) below 3.5 t.",
        Trucks:
          "Trucks are defined as heavy commercial vehicles with GVW above 3.5 t.",
      },
      regionNotes: {
        Europe:
          "Europe includes the EU27, Norway, Iceland, Switzerland and United Kingdom.",
        "Other Europe":
          "Other Europe includes Austria, Bulgaria, Croatia, Cyprus, Czech Republic, Estonia, Hungary, Ireland, Latvia; Liechtenstein, Lithuania, Luxembourg, Malta, Romania, Slovakia, Slovenia and Turkey",
      },
      evNotes:
        "BEVs are battery electric vehicles. PHEVs are plug-in hybrid electric vehicles. FCEVs are fuel cell electric vehicles. EVs refers to all electric vehicles (BEVs + PHEVs).",
      projectionNotes: {
        Historical_MoMo:
          "Historical estimates based on the IEA Mobility Model.",
        "Projection-STEPS":
          "STEPS is the Stated Policies Scenario. APS is the Announced Policies Scenario.",
        "Projection-APS":
          "STEPS is the Stated Policies Scenario. APS is the Announced Policies Scenario.",
      },
    };
  },
  computed: {
    projected() {
      return this.projectionMode === "Projected";
    },
    category() {
      if (this.projected) {
        return `Projection-${this.projection}`;
      }
      return "Historical"; /* this.historicalParameters.includes(this.parameter)
        ? "Historical"
        : "Historical_MoMo";*/
    },
    chartOptions() {
      const { areaParameters, parameter, years } = this;
      return options.buildOptions(
        {
          title: {
            text: null,
          },
          yAxis: {
            reversedStacks: true,
          },
          xAxis: {
            allowDecimals: false,
            // tickInterval: years[years.length - 1] - years[years.length - 2], // calculate interval from the last pair of years
            // tickAmount: years.length,
            floor: years[0],
            ceiling: years[years.length - 1],
          },
          plotOptions: {
            line: {
              className: "highcharts-dotted",
              marker: {
                enabled: true,
              },
            },
          },
        },
        areaParameters.includes(parameter) ? "area" : "column"
      );
    },
    notes() {
      const {
        parameterNotes,
        vehicleTypeNotes,
        regionNotes,
        evNotes,
        projectionNotes,
      } = this;
      return `${this.parameter !== "EV charger points" ? evNotes : ""}
      ${projectionNotes[this.category] || ""}
      ${parameterNotes[this.parameter] || ""}
      ${vehicleTypeNotes[this.vehicleType] || ""}
      ${regionNotes[this.region] || ""}`;
    },
    urlParams() {
      const { region, parameter, vehicleType, category } = this;
      return {
        region,
        parameter: `"${parameter}"`,
        mode: parameter === "EV charger points" ? null : vehicleType,
        category,
      };
    },
    downloadLink() {
      const params = {
        parameters: this.parameter,
        category: this.category,
      };
      if (this.parameter !== 'EV charger points') params.mode = this.vehicleType;

      const queryString = Object.entries(params)
        .map(([key, value]) => {
          if (
            typeof value === "string" &&
            value.includes(",")
          ) {
            return (
              key + "=" + encodeURIComponent(`"${value}"`)
            );
          }
          return key + "=" + encodeURIComponent(value);
        })
        .join("&")
        .concat("&csv=true");
      return `${this.apiRoute}?${queryString}`;
    },
  },
  methods: {
    sortData(data) {
      if (!data) return [];
      return [...data].sort();
    },
    lowerCase(text) {
      return `${text[0].toLowerCase()}${text.substring(1).trim()}`;
    },
    setUnit(data) {
      if (data.length === 0) return;
      this.unit = data[0] ? data[0].unit || "" : "";
      this.years = data.reduce(
        (years, row) =>
          row.year > years[years.length - 1] ? [...years, row.year] : years,
        [data[0].year]
      );
    },
    // Checks if the currently-selected item is still valid and set it to
    // the first valid value if not
    checkValid(prop, data) {
      if (data.length > 0 && !data.includes(this[prop])) {
        this[prop] = data[0];
      }
    },
  },
};
</script>
